/**
 * JWT token utilities for extracting user information
 */

/**
 * Decode JWT token payload
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url encoded payload
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Extract user roles from JWT token
 * @param {string} token - JWT token string
 * @returns {array} Array of role names
 */
export function extractUserRoles(token) {
  const payload = decodeJwtPayload(token);
  console.log('[JWT] Extracted payload:', payload);
  console.log('[JWT] User roles:', payload?.roles || []);
  return payload?.roles || [];
}

/**
 * Check if token has specific role
 * @param {string} token - JWT token string
 * @param {string} role - Role name to check
 * @returns {boolean} True if user has the role
 */
export function hasRole(token, role) {
  const roles = extractUserRoles(token);
  return roles.includes(role);
}

/**
 * Check if token belongs to admin user
 * @param {string} token - JWT token string
 * @returns {boolean} True if user is admin
 */
export function isAdmin(token) {
  return hasRole(token, 'admin');
}

/**
 * Get user ID from token
 * @param {string} token - JWT token string
 * @returns {string|null} User external ID or null
 */
export function getUserIdFromToken(token) {
  const payload = decodeJwtPayload(token);
  return payload?.sub || null;
}

/**
 * Check if token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is expired
 */
export function isTokenExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
}